// MASAR - PH.Abdallahjawadk © 2026
export type RuntimeMode = 'local' | 'cloud';
export type EnvironmentName = 'local' | 'staging' | 'production';

const requestedMode = import.meta.env.VITE_RUNTIME_MODE === 'cloud' ? 'cloud' : 'local';
const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL ?? '').trim().replace(/\/$/, '');
const rawEnvironment = import.meta.env.VITE_ENVIRONMENT;
const environment: EnvironmentName =
  rawEnvironment === 'staging' || rawEnvironment === 'production' ? rawEnvironment : 'local';

export const RUNTIME = {
  environment,
  requestedMode,
  mode: requestedMode === 'cloud' && apiBaseUrl ? 'cloud' : 'local' as RuntimeMode,
  apiBaseUrl,
  isCloudConfigured: requestedMode === 'cloud' && Boolean(apiBaseUrl),
  appBaseUrl: (import.meta.env.VITE_APP_BASE_URL ?? window.location.origin).replace(/\/$/, ''),
} as const;

// Placeholder tokens that must never survive into a deployed frontend build.
const PLACEHOLDER = /REPLACE_AFTER_PHASE_4|REPLACE_ME|your-frontend-domain\.example|<account>/;

// Runtime config validation (names only; never throws for local mode). A
// deployed (cloud) build with a missing/placeholder API base or a non-https
// endpoint is a configuration error that must fail fast.
export function validateRuntimeConfig(): { ok: boolean; errors: string[] } {
  const errors: string[] = [];
  if (RUNTIME.requestedMode === 'cloud') {
    if (!apiBaseUrl) errors.push('VITE_API_BASE_URL');
    else if (PLACEHOLDER.test(apiBaseUrl)) errors.push('VITE_API_BASE_URL (placeholder)');
    else if (!/^https:\/\//.test(apiBaseUrl)) errors.push('VITE_API_BASE_URL (must be https)');
    if (environment !== 'local' && PLACEHOLDER.test(RUNTIME.appBaseUrl)) errors.push('VITE_APP_BASE_URL (placeholder)');
  }
  return { ok: errors.length === 0, errors };
}

export function assertCloudConfigured(): void {
  if (!RUNTIME.isCloudConfigured) {
    throw new Error('Cloud mode is not configured. Set VITE_RUNTIME_MODE=cloud and VITE_API_BASE_URL.');
  }
  const { ok, errors } = validateRuntimeConfig();
  if (!ok) throw new Error(`Runtime configuration invalid: ${errors.join(', ')}`);
}
